import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  getComplimentaryExtendedAccess,
  grantComplimentaryExtendedAccess,
} from "../_redux/order/orderCrud";
import { fetchMyOrderTimeline } from "../_redux/order/orderAction";

const END_REASONS = {
  completed: "The days ran their course.",
  payment_failed: "Ended early: access was revoked for an unpaid invoice.",
  order_closed: "The order was cancelled or moved out first.",
};

/**
 * A date and time as staff read it.
 *
 * @param {string|null|undefined} value ISO timestamp.
 * @returns {string}
 */
const formatMoment = (value) =>
  value ? moment(value).format("DD-MM-yyyy HH:mm") : "-";

/**
 * Complimentary Keyless Entry System - extended on a container order: shows
 * whether the order has had its seven days of 24 hour access, when they run
 * to and who gave them, and lets staff give them (once per order).
 *
 * @param {Object} props
 * @param {number|string} props.orderId The container order.
 * @returns {JSX.Element|null}
 */
export function ComplimentaryExtendedAccessPanel({ orderId }) {
  const dispatch = useDispatch();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Load the order's state. Nothing is set if the panel has gone away.
  useEffect(() => {
    if (!orderId) return undefined;
    let current = true;

    getComplimentaryExtendedAccess(orderId)
      .then((res) => {
        if (!current) return;
        setState(res?.data?.data || null);
        setLoadFailed(false);
      })
      .catch(() => {
        if (current) setLoadFailed(true);
      });

    return () => {
      current = false;
    };
  }, [orderId, attempt]);

  if (!orderId) return null;

  // The state could not be read: say so and let staff try again, rather than
  // hiding the panel as though the order had nothing to show.
  if (loadFailed && !state) {
    return (
      <div className="card card-custom gutter-b">
        <div className="card-body py-5 d-flex justify-content-between align-items-center">
          <span role="alert" className="text-muted">
            Complimentary extended access could not be loaded.
          </span>
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => setAttempt((count) => count + 1)}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!state) return null;

  const { extension, days } = state;

  /** Close the dialog unless a request is in flight. */
  const close = () => {
    if (busy) return;
    setOpen(false);
    setError("");
  };

  /**
   * Give the complimentary days, then show the new state and reload the timeline.
   *
   * @returns {void}
   */
  const handleConfirm = () => {
    if (busy) return;
    setBusy(true);
    setError("");
    grantComplimentaryExtendedAccess(orderId)
      .then((res) => {
        setState(res?.data?.data || state);
        setOpen(false);
        dispatch(fetchMyOrderTimeline(orderId));
      })
      .catch((err) =>
        setError(
          err?.response?.data?.message ||
            "That could not be done. Please try again."
        )
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="card card-custom gutter-b">
      <div className="card-body py-5">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="font-weight-bolder text-dark mb-2">
              Complimentary extended access
            </h5>
            {!extension && (
              <div className="text-muted">
                {days} days of Keyless Entry System - extended (24 hour
                access), free of charge. Once per order.
              </div>
            )}
            {extension && (
              <div>
                <span
                  className={`badge badge-pill ${
                    extension.running ? "badge-success" : "badge-secondary"
                  } mr-2`}
                >
                  {extension.running
                    ? "Running"
                    : extension.status === "revoked"
                      ? "Ended early"
                      : "Ended"}
                </span>
                <span>
                  {formatMoment(extension.starts_at)} to{" "}
                  {formatMoment(extension.ends_at)}
                </span>
                {extension.granted_by && (
                  <span className="text-muted">
                    {" "}
                    · given by {extension.granted_by}
                  </span>
                )}
                {!extension.running && extension.end_reason && (
                  <div className="text-muted mt-1">
                    {END_REASONS[extension.end_reason] || ""}
                  </div>
                )}
              </div>
            )}
            {!extension && !state.can_grant && state.reason && (
              <div className="text-muted mt-1">{state.reason}</div>
            )}
          </div>
          {state.can_grant && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setOpen(true)}
            >
              Give {days} days
            </button>
          )}
        </div>
      </div>
      <Modal show={open} onHide={close} centered>
        <Modal.Header closeButton>
          <Modal.Title>Complimentary extended access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This gives the customer 24 hour access to their unit for {days}{" "}
            days, free of charge. After that their access goes back to 08:00
            to 17:00.
          </p>
          <p className="mb-0">
            It can be given once per order. The customer and the site manager
            are emailed, and it is noted on the order.
          </p>
          {error && (
            <div role="alert" className="text-danger mt-3">
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={close}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy ? "Saving..." : `Give ${days} days`}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
